import React, { useState, useRef, useEffect } from 'react';
import { MdOutlinePause } from 'react-icons/md';
import { Plus, ArrowUp } from 'lucide-react';

interface ChatInputProps {
	onSendMessage: (message: string) => void;
	isLoading?: boolean;
	disabled?: boolean;
	editMode?: boolean;
	editMessageId?: string;
	editContent?: string;
	onCancelEdit?: () => void;
}

function ChatInput({
	onSendMessage,
	isLoading = false,
	disabled = false,
	editMode = false,
	editMessageId,
	editContent = '',
	onCancelEdit,
}: ChatInputProps) {
	const [message, setMessage] = useState(editMode ? editContent : '');
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	// Update message when editContent changes
	useEffect(() => {
		if (editMode && editContent) {
			setMessage(editContent);
		}
	}, [editMode, editContent]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (message.trim() && !disabled) {
			// For editing, we can add a prefix to indicate it's an edited message
			// The API will handle the message_id and is_edited flag
			const messageToSend = editMode ? `[EDIT:${editMessageId}] ${message}` : message;
			onSendMessage(messageToSend);
			setMessage('');

			if (inputRef.current) {
				inputRef.current.style.height = 'auto';
			}
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	// Auto-resize textarea based on content
	const handleInput = () => {
		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
			inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
		}
	};

	// Check if user has typed something
	const hasMessage = message.trim().length > 0;

	return (
		<form onSubmit={handleSubmit} className="relative font-poppins w-full">
			<div
				className={`
          flex items-center gap-[10px]
          w-full
          p-[10px]
          rounded-[100px]
          bg-white
          border border-tertiary-background
          focus-within:ring-1 focus-within:ring-primary-500
          transition-all duration-200
          shadow-[0_0_15px_rgba(0,0,0,0.1)]
            hover:shadow-[0_0_20px_rgba(0,0,0,0.15)]
            focus-within:shadow-[0_0_25px_rgba(0,0,0,0.2)]
            backdrop-blur-sm
        `}
			>
				{/* Left Icon */}
				<button
					type="button"
					className="flex items-center justify-center w-[36px] h-[36px] rounded-[100px] hover:bg-secondary-background"
					title="Add attachment"
				>
					<Plus className="w-[20px] h-[20px] text-primary-text" />
				</button>

				{/* Input */}
				<textarea
					ref={inputRef}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					onKeyDown={handleKeyDown}
					onInput={handleInput}
					placeholder={editMode ? 'Edit your message...' : 'Ask anything'}
					className="
                flex-1
                bg-transparent
                resize-none
                focus:outline-none
                font-normal
                text-base
                text-tertiary-text
                placeholder-tertiary-text
                align-middle
                py-0
                my-0
                h-5
                min-h-5
                max-h-30
              "
					style={{
						fontWeight: 400,
						fontSize: '16px',
						lineHeight: '20px',
					}}
					rows={1}
					disabled={disabled}
					aria-label={editMode ? 'Edit message' : 'Type your message'}
				/>

				{/* Cancel Button (Edit Mode) */}
				{editMode && onCancelEdit && (
					<button
						type="button"
						onClick={onCancelEdit}
						className="shrink-0 rounded-[100px] p-2 bg-secondary-background hover:bg-gray-200 transition-all duration-200 flex items-center justify-center"
					>
						<span className="text-tertiary-text text-sm font-medium">Cancel</span>
					</button>
				)}

				{/* Send Button */}
				<button
					type="submit"
					disabled={!hasMessage || disabled}
					className={`
            shrink-0
            rounded-[100px]
            p-2
            opacity-100
            flex items-center justify-center
            transition-all duration-200
            ${
				isLoading
					? 'bg-primary'
					: hasMessage && !disabled
					? 'bg-primary hover:bg-blue-600 hover:scale-105'
					: 'bg-secondary-background'
			}
            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          `}
				>
					{isLoading ? (
						<MdOutlinePause className="w-[20px] h-[20px] text-white" />
					) : (
						<ArrowUp
							className={`w-[20px] h-[20px] ${hasMessage ? 'text-white' : 'text-black'}`}
							strokeWidth={1.75}
						/>
					)}
				</button>
			</div>
		</form>
	);
}

export default ChatInput;
